import {FC, PropsWithChildren, ReactNode, useEffect, useState} from "react";
import {ActionIcon, CloseButton, Drawer, DrawerProps, Flex, Tooltip} from "@mantine/core";
import {useViewportSize, useWindowEvent} from "@mantine/hooks";
import {IconArrowsDiagonal, IconArrowsDiagonalMinimize2} from "@tabler/icons-react";
import {useOverlayLayer} from "@/utils/atoms";
import hubspotService from "@/services/hubspot/hubspot-service";

const defaultWidth = 530

/** Size of a drawer in full-screen mode: the whole viewport */
export const fullscreenDrawerSize = '100%'

/**
 * Whether the full-screen toggle is worth offering: always while full-screen (to get back), otherwise only
 * when the drawer is narrower than the screen. CSS sizes (e.g. clamp(900px, 100vw - 310px, 1320px)) can't be
 * compared to the viewport here, and those drawers are at least ~900px wide, so they get it on wider screens.
 */
export const canExpandDrawer = (size: DrawerProps['size'], screenWidth: number, fullscreen: boolean) =>
    fullscreen || (typeof size === 'number' ? screenWidth > size : screenWidth > 1000)

/** The expand / restore toggle next to a drawer's close button. Also used by drawers with their own header. */
export const DrawerFullscreenButton: FC<{ fullscreen: boolean, onToggle: () => void }> = ({fullscreen, onToggle}) =>
    <Tooltip color={'scBlue'} label={fullscreen ? 'Exit full-screen' : 'Full-screen'} openDelay={700}>
        <ActionIcon
            variant={'subtle'}
            color={'gray.7'}
            aria-label={fullscreen ? 'Exit full-screen' : 'Full-screen'}
            onClick={onToggle}
        >
            {fullscreen ? <IconArrowsDiagonalMinimize2 size={18}/> : <IconArrowsDiagonal size={18}/>}
        </ActionIcon>
    </Tooltip>

// The HubSpot widget (and our custom hover-minimise button on it) render as siblings in
// the main document, not inside the drawer - a click there is otherwise indistinguishable
// from a genuine outside click and would close the drawer instead of just toggling the chat.
const hubspotElementIds = new Set(['hubspot-messages-iframe-container', 'hide-hs-overlay']);

// helper to find if modal was clicked
const isAcceptedElementOrParent = (element: HTMLElement | null): boolean => {
    while (element) {
        if (element.classList.contains('mantine-Drawer-content')) return true; // clicks inside the drawer itself should never close it
        if (element.classList.contains('mantine-Modal-root')) return true;
        if (element.classList.contains('mantine-Combobox-dropdown')) return true; // dropdowns are usually in portals so this prevents accidental closing
        if (element.classList.contains('mantine-Menu-dropdown')) return true; // dropdowns are usually in portals so this prevents accidental closing
        if (element.classList.contains('mantine-Popover-dropdown')) return true; // date pickers render their calendar in a portal popover
        if (element.classList.contains('sc-customer-change-modal')) return true; // customer change modal renders in a portal
        if (element.id && hubspotElementIds.has(element.id)) return true; // hubspot chat widget / its hover-minimise button
        if (element.nodeName === 'A') return true;
        element = element.parentElement;
    }
    return false;
};

const ScDrawer: FC<PropsWithChildren<DrawerProps &
    {
        title?: string | ReactNode;
        // default = false
        linkToFullPage?: false | string;
        // shows the full-screen toggle next to the close button
        showFullscreenExpandButton?: boolean;
        // controlled full-screen state (otherwise ScDrawer keeps its own); either way it resets when the drawer closes
        fullscreen?: boolean;
        onFullScreenModeChanges?: (fullscreen: boolean) => void;
    }>> = (
    {
        children,
        showFullscreenExpandButton,
        fullscreen: controlledFullscreen,
        onFullScreenModeChanges,
        linkToFullPage,
        ...drawerProps
    }
) => {

    // attempt to close when click away, but can become problematic when not allowed to close


    const {width: screenWidth} = useViewportSize()

    const [ownFullscreen, setOwnFullscreen] = useState(false);
    const fullscreen = controlledFullscreen ?? ownFullscreen
    const setFullscreen = (value: boolean) => {
        setOwnFullscreen(value)
        onFullScreenModeChanges && onFullScreenModeChanges(value)
    }

    // a drawer always opens at its normal size
    useEffect(() => {
        if (!drawerProps.opened && fullscreen) setFullscreen(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerProps.opened])

    const normalWidth = drawerProps.size ?? defaultWidth
    const width = fullscreen ? fullscreenDrawerSize : normalWidth

    const {isTopMost} = useOverlayLayer(!!drawerProps.opened) // tracks whether some other drawer/modal is stacked on top of this one

    /**
     * closeOnOutsideClick prop does not work when overlay is removed.
     * using element ref for outside click detection does not work as portals are often used withing wrapped components.
     *
     * this solution determines an outside click by walking up from the clicked element:
     * a click is considered "inside" (and won't close the drawer) when it lands on the drawer content,
     * on anchors or links (to keep the drawer open when a link updates drawer content) or on portaled modals/menus/dropdowns,
     * any other click closes the drawer. This avoids relying on the (now CSS based) drawer width.
     *
     * isTopMost guards against closing a drawer that isn't the topmost open layer - otherwise an
     * outside click meant for a drawer/modal stacked on top (e.g. a preferences drawer) would also
     * close this one underneath it.
     **/
    useWindowEvent('mousedown', (e) => {
        if (document && typeof document !== 'undefined') {
            const offsetX = e.clientX /*(offsetX will be 0 when clicked using Space or Enter keypress)*/

            if(!!offsetX) { // ensure offsetX exists - this will avoid accidental closes resulting from keyboard interactions
                const clickedElement = e.target as HTMLElement
                const keepOpen = isAcceptedElementOrParent(clickedElement) // keepOpen is true for clicks inside the drawer/portaled menus/anchors; any other click is treated as an outside click

                if (!keepOpen) {
                    drawerProps.closeOnClickOutside !== false && drawerProps.opened && isTopMost &&
                    drawerProps.onClose()
                }
            }
        }
    }, {
        once: false,
        capture: true,
    });

    const chatSafeAreaBottom = hubspotService.useChatSafeAreaBottom()

    return (
        <Drawer
            overlayProps={{
                color: 'var(--mantine-color-scBlue-5)',
                blur: 10,
                opacity: .25
            }}
            position={'right'}
            {...drawerProps}
            size={width}
            styles={{
                ...drawerProps.styles,
                body: {
                    paddingBottom: chatSafeAreaBottom,
                    ...drawerProps.styles?.body,
                },
                title: {
                    fontSize: 24,
                    fontWeight: 600,
                    color: 'var(--mantine-color-scBlue-9)',
                    width: '100%',
                    ...drawerProps.styles?.title,
                },
                content: {
                    borderLeft: '1px solid var(--mantine-color-gray-4)',
                    boxShadow: '-1px 0 3px 2px var(--mantine-color-gray-4)',
                    ...drawerProps.styles?.content,
                }
            }}
            title={<Flex align={'center'} justify={'space-between'} w={'100%'}>
                {drawerProps.title}
                <Flex ml={'auto'} gap={'xs'} align={'center'}>
                    {
                        showFullscreenExpandButton && canExpandDrawer(normalWidth, screenWidth, fullscreen) &&
                        <DrawerFullscreenButton fullscreen={fullscreen} onToggle={() => setFullscreen(!fullscreen)}/>
                    }
                    {
                        drawerProps.withCloseButton !== false &&
                        // initial focus stays on close (as before the full-screen toggle was added), so the toggle's tooltip
                        // doesn't open on focus and swallow the first Escape
                        <CloseButton onClick={drawerProps.onClose} aria-label={'Close'} data-autofocus/>
                    }
                </Flex>
            </Flex>}
            keepMounted={false}
            withOverlay={false}
            closeOnClickOutside={false} // doesn't work when overlay is disabled
            withCloseButton={false}
            closeOnEscape={isTopMost}
        >
            {drawerProps.opened && children}
        </Drawer>
    )
};

export default ScDrawer


